import React from 'react';

const faxStates = {
  sent: {
    color: '#9CA3AF',
    tooltip: 'Fax sent — awaiting response',
    label: 'sent'
  },
  received: {
    color: '#3B82F6',
    tooltip: 'All faxes received — awaiting processor review',
    label: 'received'
  },
  partial: {
    color: '#9CA3AF',
    tooltip: 'Some faxes received — awaiting remaining jurisdictions',
    label: 'partial'
  },
  reviewed: {
    color: '#22C55E',
    tooltip: 'Fax reviewed by processor',
    label: 'reviewed'
  }
};

const FaxIcon = ({ color, size = 14 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 16V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" />
    <path d="M6 20h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2z" />
    <path d="M6 14h4" />
    <path d="M14 14h2" />
    <path d="M6 18h4" />
  </svg>
);

/**
 * Compute aggregate fax status from fax_deliveries array.
 * 
 * Rules:
 * - No deliveries with "sent"/"received" status → use legacy fax_status as-is
 * - All deliveries received → "received"
 * - Some received, some pending → "sent" (stay in-progress)
 * - All reviewed → "reviewed"  
 * - Otherwise → use legacy fax_status
 */
function computeAggregateFaxStatus(faxStatus, faxDeliveries) {
  // If no deliveries data, fall back to legacy field
  if (!faxDeliveries || !Array.isArray(faxDeliveries) || faxDeliveries.length === 0) {
    return faxStatus || null;
  }

  // Filter to only actionable entries (sent, received, sending — exclude failed, not_configured)
  const actionable = faxDeliveries.filter(d =>
    d.status === 'sent' || d.status === 'received' || d.status === 'sending'
  );

  // No actionable fax deliveries → fall back to legacy
  if (actionable.length === 0) {
    return faxStatus || null;
  }

  const allReceived = actionable.every(d => d.status === 'received');
  const someReceived = actionable.some(d => d.status === 'received');

  // If legacy status is "reviewed", respect it (file acknowledgement is order-level)
  if (faxStatus === 'reviewed') {
    return 'reviewed';
  }

  // All faxes received → show "received" (blue)
  if (allReceived) {
    return 'received';
  }

  // Some received but not all → stay "sent" (gray, in-progress)
  // This is the key fix: partial receipt must NOT show received color
  if (someReceived) {
    return 'sent';
  }

  // All still sent/sending → "sent"
  return 'sent';
}

export const FaxIndicator = ({ faxStatus, faxDeliveries }) => {
  const aggregateStatus = computeAggregateFaxStatus(faxStatus, faxDeliveries);

  if (!aggregateStatus || !faxStates[aggregateStatus]) return null;

  const state = faxStates[aggregateStatus];

  return (
    <span
      className="inline-flex items-center ml-1.5"
      title={state.tooltip}
      data-testid={`fax-indicator-${aggregateStatus}`}
    >
      <FaxIcon color={state.color} size={14} />
    </span>
  );
};

export default FaxIndicator;
